import { Button, Loader } from '@mantine/core';
import { ArrowDown } from 'lucide-react';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import SlotAdjustmentModal from '@/components/AppointmentScheduler/SlotAdjustmentModal.tsx';
import { ChainBookingSummary } from '@/components/chain-booking/chain-booking-summary.tsx';
import { ChainStepCard } from '@/components/chain-booking/chain-step-card.tsx';
import { ChainSuggestionEmpty } from '@/components/chain-booking/chain-suggestion-empty.tsx';
import Heading4 from '@/components/text/Heading4.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { useAppointmentChainConfigs } from '@/hooks/admin/use-appointment-chain-configs.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerData } from '@/hooks/useEnrichedPractitionerData.ts';

import { Insurance } from '@/models/appointment/Appointment.ts';
import { ChainBookingStep, ChainPlanningMode } from '@/models/appointment/AppointmentChainConfig.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';

import {
    applySlotUpdate,
    computeChainStatuses,
    countUnavailableSteps,
    getEffectiveGap,
} from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { formatGapRange, parseGapBound } from '@/utils/duration-utils.ts';
import { INSURANCE_TYPE_TO_INSURANCE } from '@/utils/insurance-utils.ts';
import {
    buildAppointmentTypeDetailMap,
    getPractitionerByCalendarId,
} from '@/utils/practitioner/bookingFormOptionsUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

interface Props {
    readOnly?: boolean;
}

export const ChainBookingSlotTuner = ({ readOnly = false }: Props) => {
    const { customer_id: customerId } = useConfig();
    const {
        chainConfigs,
        isLoading: isChainsLoading,
        error: chainsError,
        mutate: refetchChains,
    } = useAppointmentChainConfigs({ customerId });
    const { data: practitionerData, is_loading: isPractitionerLoading } = useEnrichedPractitionerData();

    const {
        chain_id: chainId,
        insurance_type: insuranceType,
        start_date: startDate,
        removed_orders: removedOrders,
        calendar_selections: calendarSelections,
    } = useBookingStore(useShallow((state) => state.chain_selection));
    const availability = useBookingStore((state) => state.chain_availability);
    const currentPatient = useBookingStore((state) => state.current_patient);
    const setChainAvailability = useBookingStore((state) => state.setChainAvailability);
    const setChainBookingStep = useBookingStore((state) => state.setChainBookingStep);
    const setChainPlanningMode = useBookingStore((state) => state.setChainPlanningMode);

    const [editingStepOrder, setEditingStepOrder] = useState<number>();

    const appointmentTypeDetails = buildAppointmentTypeDetailMap(practitionerData);
    const allPractitioners = (practitionerData ?? []).flatMap((i) => i.practitioners ?? []);

    const selectedChain = chainConfigs.find((c) => c.id === chainId);
    const visibleSteps = selectedChain?.steps.filter((s) => !removedOrders.includes(s.order)) ?? [];
    const availabilityByOrder = new Map(availability.map((item) => [item.step_order, item]));

    const allOriginalOrders = selectedChain?.steps.map((s) => s.order) ?? [];
    const statuses = computeChainStatuses(availability, visibleSteps, selectedChain);
    const unavailableCount = countUnavailableSteps(statuses);
    const availabilityMismatch = availability.length !== visibleSteps.length;
    const isEmptySuggestion = readOnly && availability.length === 0;
    const patientFullName = currentPatient
        ? `${currentPatient.personal.last_name} ${currentPatient.personal.first_name}`
        : '—';

    const editingStep =
        editingStepOrder === undefined ? undefined : visibleSteps.find((s) => s.order === editingStepOrder);
    const editingItem = editingStepOrder === undefined ? undefined : availabilityByOrder.get(editingStepOrder);
    const editingPractitioner = editingStep
        ? getPractitionerByCalendarId(allPractitioners, calendarSelections[editingStep.order])
        : undefined;
    const editingAppointmentType = editingStep
        ? (practitionerData ?? [])
              .flatMap((instance) => instance.appointment_types ?? [])
              .find((apt) => apt.id === editingStep.appointment_type_id)
        : undefined;
    const insuranceForModal = insuranceType ? INSURANCE_TYPE_TO_INSURANCE[insuranceType] : Insurance.PUBLIC;

    const handleEditStep = (order: number): void => setEditingStepOrder(order);
    const handleCloseEditModal = (): void => setEditingStepOrder(undefined);

    const handleBookManually = (): void => {
        setChainPlanningMode(ChainPlanningMode.MANUAL);
        setChainBookingStep(ChainBookingStep.CONFIGURE);
    };

    const handleSlotSelect = (slot: string): void => {
        if (!editingStep || !editingItem) return;
        const durationMinutes = appointmentTypeDetails[editingStep.appointment_type_id]?.durationMinutes ?? 0;
        const latest = useBookingStore.getState().chain_availability;
        setChainAvailability(applySlotUpdate(latest, editingStep.order, slot, durationMinutes));
        setEditingStepOrder(undefined);
    };

    if (isChainsLoading || isPractitionerLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader />
            </div>
        );
    }

    const handleRetryChains = (): void => {
        void refetchChains();
    };

    if (chainsError) {
        return (
            <div className="flex flex-col items-center gap-3 py-8">
                <Paragraph className="text-sm text-red-500">Ketten konnten nicht geladen werden.</Paragraph>
                <Button size="sm" variant="default" onClick={handleRetryChains}>
                    Erneut versuchen
                </Button>
            </div>
        );
    }

    if (!selectedChain) {
        return (
            <Paragraph className="text-sm text-red-500">
                Keine Terminkette ausgewählt. Bitte gehen Sie zurück zur Konfiguration.
            </Paragraph>
        );
    }

    if (isEmptySuggestion) {
        return (
            <div className="flex flex-col gap-6">
                <Heading4 className="text-base font-semibold">Termine bestätigen</Heading4>
                <ChainSuggestionEmpty onBookManually={handleBookManually} />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <Heading4 className="text-base font-semibold">Termine bestätigen</Heading4>

            <ChainBookingSummary
                chainName={selectedChain.name.de}
                patientName={patientFullName}
                appointmentCount={visibleSteps.length}
                startDateLabel={dayjs(availabilityByOrder.get(visibleSteps[0]?.order)?.start_date ?? startDate).format(
                    'D. MMM YYYY'
                )}
                conflictCount={unavailableCount}
            />

            {availabilityMismatch && (
                <Paragraph className="text-sm text-red-500">
                    Verfügbarkeitsdaten sind inkonsistent. Bitte zurück und erneut versuchen.
                </Paragraph>
            )}

            <div className="flex flex-col">
                <Paragraph className="mb-2 text-sm font-medium">Alle Termine</Paragraph>
                <div className="flex max-h-[calc(75vh-200px)] flex-col overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200">
                    {visibleSteps.map((step, index) => {
                        const item = availabilityByOrder.get(step.order);
                        const aptType = appointmentTypeDetails[step.appointment_type_id];
                        const practitioner = getPractitionerByCalendarId(
                            allPractitioners,
                            calendarSelections[step.order] ?? item?.calendar_id
                        );
                        const previousStep = visibleSteps[index - 1];
                        const gapLabel =
                            previousStep && selectedChain.gaps.length > 0
                                ? (() => {
                                      const g = getEffectiveGap(
                                          selectedChain.gaps,
                                          previousStep.order,
                                          step.order,
                                          allOriginalOrders
                                      );
                                      if (g.max_duration === undefined && parseGapBound(g.min_duration).value === 0)
                                          return undefined;
                                      return formatGapRange(g.min_duration, g.max_duration, 'de');
                                  })()
                                : undefined;
                        return (
                            <div key={step.order} className={cn('relative', index > 0 && 'border-t border-slate-200')}>
                                {gapLabel && (
                                    <div className="absolute -top-2.5 left-3 z-10 inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] leading-none text-slate-500">
                                        <ArrowDown className="size-3" />
                                        <span>{gapLabel}</span>
                                    </div>
                                )}
                                <ChainStepCard
                                    position={index + 1}
                                    stepOrder={step.order}
                                    appointmentTypeName={aptType?.name ?? 'Unbekannte Terminart'}
                                    practitionerName={practitioner?.name ?? '—'}
                                    startDatetime={item?.start_date}
                                    durationMinutes={aptType?.durationMinutes ?? 0}
                                    status={statuses[index]}
                                    onEdit={!readOnly && item ? handleEditStep : undefined}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>

            {editingStep && editingItem && editingPractitioner && editingAppointmentType && (
                <SlotAdjustmentModal
                    opened
                    onClose={handleCloseEditModal}
                    initialDate={editingItem.start_date}
                    selectedAppointmentType={editingAppointmentType}
                    practitioner={editingPractitioner}
                    selectedCalendar={calendarSelections[editingStep.order] ?? editingItem.calendar_id}
                    insurance={insuranceForModal}
                    onSlotSelect={handleSlotSelect}
                    mode="chain"
                />
            )}
        </div>
    );
};
